import type { RootState } from "./store";
import type { ThunkAction, AnyAction } from "@reduxjs/toolkit";
import { FolderTypeEnum, VisualMediaClassificationEnum } from "../types/models";

// Owned by the store, released after settlement; no event/progress history is retained.
const requests = new WeakMap<
  Function,
  Map<string, { promise: Promise<void>; started: boolean; dirty: boolean }>
>();
export const refreshStreamSnapshot =
  (streamId: string): ThunkAction<Promise<void>, unknown, unknown, AnyAction> =>
  (dispatch, getState) => {
    let streams = requests.get(getState);
    if (!streams) {
      streams = new Map();
      requests.set(getState, streams);
    }
    const current = streams.get(streamId);
    if (current) {
      if (current.started) current.dirty = true;
      return current.promise;
    }
    const request = {
      promise: Promise.resolve(),
      started: false,
      dirty: false,
    };
    streams.set(streamId, request);
    request.promise = Promise.resolve()
      .then(async () => {
        // Mutating slices also request repair; resolve their thunks at execution time
        // to keep module initialization independent of that dependency cycle.
        const [
          { getLiveStreamById },
          { getEntitlement },
          { getStreamOperations },
          { getAudioList, getVisualMediaList },
          { getFolders },
        ] = await Promise.all([
          import("./Slices/LiveStreamSlice"),
          import("./Slices/EntitlementSlice"),
          import("./Slices/OperationsSlice"),
          import("./Slices/MediaSlice"),
          import("./Slices/FolderSlice"),
        ]);
        // One initial pass plus at most one follow-up for invalidation during flight.
        // Each HTTP request has a finite timeout. A persistent outage retries on the visible poll.
        for (let pass = 0; pass < 2; pass++) {
          request.started = true;
          request.dirty = false;
          const state = getState() as RootState;
          const work: Promise<unknown>[] = [
            dispatch(getEntitlement({ streamId, silent: true })),
            dispatch(getStreamOperations(streamId)),
          ];
          // Background mutations for A must not replace the detail/list being displayed for B.
          const detailId = state.liveStream?.liveStreamByIdRequestedId;
          if (
            (!detailId || detailId === streamId) &&
            !state.liveStream?.liveStreamByIdLoading
          )
            work.push(
              dispatch(getLiveStreamById({ id: streamId, silent: true }))
            );
          const context =
            state.media?.itemsStreamId === streamId &&
            state.media.itemsContextKey
              ? JSON.parse(state.media.itemsContextKey)
              : !detailId || detailId === streamId
              ? [
                  streamId,
                  FolderTypeEnum.video,
                  null,
                  "",
                  1,
                  VisualMediaClassificationEnum.playlistVideo,
                ]
              : null;
          if (context) {
            const [owner, type, folderId, query, pageNumber, classification] =
              context;
            if (owner === streamId) {
              const args = {
                streamId,
                folderId: folderId ?? undefined,
                query,
                pageNumber,
              };
              if (!state.media?.getItemsLoading)
                work.push(
                  type === FolderTypeEnum.audio
                    ? dispatch(getAudioList({ ...args, background: true }))
                    : dispatch(
                        getVisualMediaList({
                          ...args,
                          background: true,
                          classification:
                            classification ??
                            VisualMediaClassificationEnum.playlistVideo,
                        })
                      )
                );
              if (!state.folder?.loading)
                work.push(
                  dispatch(getFolders({ streamId, type, background: true })),
                );
            }
          }
          await Promise.all(work);
          if (!request.dirty) break;
        }
      })
      .finally(() => {
        streams!.delete(streamId);
      });
    return request.promise;
  };

/** A settlement must get a read taken after POST, even if the old coalesced
 * snapshot has already used its single follow-up pass. */
export const refreshStreamSnapshotAfterMutation =
  (streamId: string): ThunkAction<Promise<void>, unknown, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const previous = requests.get(getState)?.get(streamId);
    if (previous) await previous.promise;
    await dispatch(refreshStreamSnapshot(streamId));
  };
